import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getAllUsers, 
  makeUserAdmin, 
  revokeAdminRole,
  updateUserCoins,
  updateUserXP,
  changeUserPassword,
  deleteUser,
  runCleanupJob
} from '../services/api';
import './AdminTaskReports.css'; // Reuse the admin reports styling
import './AdminUserManagement.css';

const AdminUserManagement = ({ standalone = true }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const hasInitialized = useRef(false);
  
  // Modal states
  const [showEditCoinsModal, setShowEditCoinsModal] = useState(false);
  const [showEditXPModal, setShowEditXPModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [newCoins, setNewCoins] = useState('');
  const [newXP, setNewXP] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Check if user is admin, if not redirect
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }

    // Fetch users
    if (!hasInitialized.current) {
      console.log('Initial user fetch');
      fetchUsers();
      hasInitialized.current = true;
    }
  }, [user, navigate]);
  
  // If component is mounted and visible, fetch users
  useEffect(() => {
    if (user && user.role === 'admin') {
      console.log('Component is mounted, fetching users');
      fetchUsers();
    }
  }, []);

  // Fetch all users from the server
  const fetchUsers = async () => {
    console.log('Fetching users...');
    setLoading(true);
    setError(null); // Clear any previous errors
    
    try {
      const usersData = await getAllUsers();
      console.log('Users data received:', usersData ? usersData.length : 0, 'users');
      setUsers(usersData || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Expose fetchUsers to be called from the UI
  const handleRefreshUsers = () => {
    fetchUsers();
  };

  // Handle making a user an admin
  const handleMakeAdmin = async (userId) => {
    try {
      await makeUserAdmin(userId);
      // Update the user in the local state
      setUsers(users.map(u => 
        u._id === userId ? { ...u, role: 'admin' } : u
      ));
    } catch (err) {
      console.error('Error making user admin:', err);
      alert('Failed to update user role. Please try again.');
    }
  };

  // Handle revoking admin privileges
  const handleRevokeAdmin = async (userId) => {
    try {
      await revokeAdminRole(userId);
      // Update the user in the local state
      setUsers(users.map(u => 
        u._id === userId ? { ...u, role: 'user' } : u
      ));
    } catch (err) {
      console.error('Error revoking admin role:', err);
      alert('Failed to update user role. Please try again.');
    }
  };

  // Handle opening edit coins modal
  const handleEditCoins = (user) => {
    setSelectedUser(user);
    setNewCoins(user.wrCoins.toString());
    setShowEditCoinsModal(true);
  };

  // Handle updating user coins
  const handleUpdateCoins = async () => {
    try {
      const coins = parseFloat(newCoins);
      if (isNaN(coins) || coins < 0) {
        alert('Please enter a valid number of coins (0 or greater)');
        return;
      }
      
      const response = await updateUserCoins(selectedUser._id, coins);
      
      // Update the user in the local state
      setUsers(users.map(u => 
        u._id === selectedUser._id ? response.user : u
      ));
      
      // Close modal and reset form
      setShowEditCoinsModal(false);
      setSelectedUser(null);
      setNewCoins('');
      
      alert(response.message);
    } catch (err) {
      console.error('Error updating user coins:', err);
      alert('Failed to update user coins. Please try again.');
    }
  };

  // Handle opening edit XP modal
  const handleEditXP = (user) => {
    setSelectedUser(user);
    setNewXP(user.xp.toString());
    setShowEditXPModal(true);
  };

  // Handle updating user XP
  const handleUpdateXP = async () => {
    try {
      const xp = parseInt(newXP);
      if (isNaN(xp) || xp < 0) {
        alert('Please enter a valid XP amount (0 or greater)');
        return;
      }
      
      const response = await updateUserXP(selectedUser._id, xp);
      
      // Update the user in the local state
      setUsers(users.map(u => 
        u._id === selectedUser._id ? response.user : u
      ));
      
      // Close modal and reset form
      setShowEditXPModal(false);
      setSelectedUser(null);
      setNewXP('');
      
      alert(response.message);
    } catch (err) {
      console.error('Error updating user XP:', err);
      alert('Failed to update user XP. Please try again.');
    }
  };

  // Handle opening change password modal
  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowChangePasswordModal(true);
  };

  // Handle changing user password
  const handleUpdatePassword = async () => {
    try {
      if (!newPassword || newPassword.length < 6) {
        alert('Password must be at least 6 characters long');
        return;
      }
      
      const response = await changeUserPassword(selectedUser._id, newPassword);
      
      // Close modal and reset form
      setShowChangePasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
      
      alert(response.message);
    } catch (err) {
      console.error('Error changing user password:', err);
      alert('Failed to change user password. Please try again.');
    }
  };

  // Handle deleting user
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.username}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await deleteUser(user._id);
      
      // Remove user from local state
      setUsers(users.filter(u => u._id !== user._id));
      
      alert(response.message);
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Failed to delete user. Please try again.');
    }
  };

  // Handle running cleanup job
  const handleRunCleanup = async () => {
    if (!window.confirm(`Are you sure you want to run the cleanup job? This will clean up inactive defense systems for users inactive for 4+ months.`)) {
      return;
    }
    
    setCleanupLoading(true);
    
    try {
      const response = await runCleanupJob();
      
      alert(`Cleanup job completed successfully!\n\nExecuted by: ${response.executedBy}\nExecuted at: ${new Date(response.executedAt).toLocaleString()}`);
    } catch (err) {
      console.error('Error running cleanup job:', err);
      alert('Failed to run cleanup job. Please try again.');
    } finally {
      setCleanupLoading(false);
    }
  };

  // Close modals
  const closeModals = () => {
    setShowEditCoinsModal(false);
    setShowEditXPModal(false);
    setShowChangePasswordModal(false);
    setSelectedUser(null);
    setNewCoins('');
    setNewXP('');
    setNewPassword('');
  };


  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`admin-reports-container ${!standalone ? 'embedded' : ''}`}>
      {standalone && (
        <div className="admin-reports-header">
          <h1>User Management</h1>
        </div>
      )}

      <div className="admin-search">
        <input 
          type="text"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button 
          className="admin-action-button"
          onClick={handleRefreshUsers}
          disabled={loading}
          style={{ marginLeft: '10px' }}
        >
          {loading ? 'Loading...' : 'Refresh Users'}
        </button>
        <button 
          className="admin-action-button cleanup-button"
          onClick={handleRunCleanup}
          disabled={cleanupLoading}
          style={{ marginLeft: '10px', backgroundColor: '#ff6b35' }}
          title="Run cleanup job for inactive users (4+ months)"
        >
          {cleanupLoading ? '🧹 Running...' : '🧹 Run Cleanup'}
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="users-list">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>XP</th>
                <th>WR Coins</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user._id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>{user.xp}</td>
                  <td>{user.wrCoins}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="edit-coins-button"
                        onClick={() => handleEditCoins(user)}
                        title="Edit WR Coins"
                      >
                        💰
                      </button>
                      <button 
                        className="edit-xp-button"
                        onClick={() => handleEditXP(user)}
                        title="Edit XP"
                      >
                        ⭐
                      </button>
                      <button 
                        className="change-password-button"
                        onClick={() => handleChangePassword(user)}
                        title="Change Password"
                      >
                        🔑
                      </button>
                      {user.role === 'user' ? (
                        <button 
                          className="make-admin-button"
                          onClick={() => {
                            if (window.confirm(`Make ${user.username} an admin?`)) {
                              handleMakeAdmin(user._id);
                            }
                          }}
                          title="Make Admin"
                        >
                          👑
                        </button>
                      ) : (
                        <button 
                          className="revoke-admin-button"
                          onClick={() => {
                            if (window.confirm(`Revoke admin role from ${user.username}?`)) {
                              handleRevokeAdmin(user._id);
                            }
                          }}
                          title="Revoke Admin"
                        >
                          👤
                        </button>
                      )}
                      {user.role !== 'admin' && (
                        <button 
                          className="delete-user-button"
                          onClick={() => handleDeleteUser(user)}
                          title="Delete User"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Coins Modal */}
      {showEditCoinsModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit WR Coins for {selectedUser.username}</h3>
            <div className="form-group">
              <label>WR Coins:</label>
              <input
                type="number"
                value={newCoins}
                onChange={(e) => setNewCoins(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>
            <div className="modal-buttons">
              <button onClick={handleUpdateCoins} className="admin-action-button">
                Update Coins
              </button>
              <button onClick={closeModals} className="cancel-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit XP Modal */}
      {showEditXPModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit XP for {selectedUser.username}</h3>
            <div className="form-group">
              <label>XP:</label>
              <input
                type="number"
                value={newXP}
                onChange={(e) => setNewXP(e.target.value)}
                min="0"
                step="1"
              />
            </div>
            <div className="modal-buttons">
              <button onClick={handleUpdateXP} className="admin-action-button">
                Update XP
              </button>
              <button onClick={closeModals} className="cancel-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Change Password for {selectedUser.username}</h3>
            <div className="form-group">
              <label>New Password (min 6 characters):</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength="6"
              />
            </div>
            <div className="modal-buttons">
              <button onClick={handleUpdatePassword} className="admin-action-button">
                Change Password
              </button>
              <button onClick={closeModals} className="cancel-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;